// §4.10 — THE PUBLIC QUOTE PAGE, route `/q/$token`.
//
// The one screen in this product a customer sees. It is unauthenticated by
// construction: the token IS the credential (the `/accept-invite` precedent),
// it renders outside AppShell and outside any session guard, and it is opened
// from a Telegram message on a phone.
//
// FIVE STATES, FIVE DESIGNED PAGES — not one page with conditionals. §4.10
// names valid / expired / superseded / already-accepted / not-found, and each
// is a different sentence to a different person in a different situation:
//
//   valid       the offer, its lines, a countdown, Accept and Decline.
//   expired     "ask your manager for a new offer", with the manager's own
//               contact link — the ONLY useful thing this page can do.
//   superseded  a newer offer was sent, dated, with the manager to ask. It
//               deliberately does NOT forward: the token is stored as a hash,
//               so the newer link cannot be reconstructed without publishing
//               raw acceptance credentials into the database (the contract's
//               `superseded` member carries the amendment).
//   accepted    the order number and the tracking link — which is also what a
//               §9.1 double-tap loser gets, so the second tap of an impatient
//               thumb reads as success rather than as an error.
//   not-found   a generic 404 with NO tenant name and NO branding. It is the
//               state a mistyped or probed token reaches, and anything on it
//               that identifies a customer of ours is a leak.
//
// A SIXTH THING can happen — the read fails — and it is deliberately NOT one
// of the five: telling a customer their offer does not exist because our load
// balancer hiccupped is the worst sentence this page could produce. It gets a
// retry card that says the offer is unaffected (lib/public-view.ts states the
// rule; `quoteViewFromError` is where only a 404 becomes "not found").
//
// WHAT IS NOT HERE, AND CANNOT BE: cost, margin, guard, price source, internal
// note, revision number, owner name. Not omitted per role — ABSENT FROM THE
// PAYLOAD SCHEMA (`PublicQuoteLine` is its own type, not a pick of
// `QuoteLine`), so a serializer change cannot leak one into this render.

import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { CardSkeleton } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { formatMinor } from "../../office/inventory/lib/cost";
import { formatBusinessDate, formatTimestamp } from "../../../lib/datetime";
import { RequestError } from "../../../lib/request";
import { PublicFailure, PublicShell } from "../public-shell";
import {
  countdownFrom,
  isSafeInternalPath,
  publicRenderLocale,
  type PublicQuoteView,
  type PublicSeller,
} from "../lib/public-view";
import {
  useAcceptPublicQuote,
  useDeclinePublicQuote,
  usePublicQuote,
} from "../hooks/public-queries";

type DeclineReason = "price" | "lead_time" | "stock" | "other";

const DECLINE_REASONS: readonly { value: DeclineReason; key: string }[] = [
  { value: "price", key: "public.quote.declineWhyPrice" },
  { value: "lead_time", key: "public.quote.declineWhyLeadTime" },
  { value: "stock", key: "public.quote.declineWhyStock" },
  { value: "other", key: "public.quote.declineWhyOther" },
];

/** The manager's contact button — the expired page's whole point, and the
 * valid page's escape hatch. Absent when the manager published no channel;
 * absence, never a dead control.
 *
 * WITH NO URL THE NAME IS STILL THE ANSWER. `manager_contact_url` is NULL for
 * the whole of P3 by decision (§4.10: a sales manager's only channel is their
 * personal Telegram identity, and publishing it to an external customer is a
 * privacy decision nobody made) — and the same clause promises that "the
 * expired page names whom to ask for". It did not: `manager_name` was rendered
 * ONLY as the text of the link, so every page that mattered returned null here
 * and a customer read "Ask your manager for a new offer" without ever being
 * told which person that is. On an unauthenticated page reached from a
 * forwarded message, "your manager" names nobody. */
function ManagerContact({ seller }: { seller: PublicSeller }) {
  const { t } = useTranslation();
  if (seller.manager_contact_url === null) {
    if (seller.manager_name === null) return null;
    return (
      <p className="public-contact" data-testid="public-manager-name">
        {t("public.quote.yourManager", { name: seller.manager_name })}
      </p>
    );
  }
  return (
    <p className="public-contact">
      <a
        className="public-link"
        href={seller.manager_contact_url}
        // A customer-facing outbound link to a messenger: `noopener` because
        // the target must not reach back into this tab, `noreferrer` because
        // the token is in this page's URL and a Referer header would hand the
        // credential to Telegram's web view.
        rel="noopener noreferrer"
        target="_blank"
        data-testid="public-manager-contact"
      >
        {seller.manager_name ?? t("public.quote.contactManager")}
      </a>
    </p>
  );
}

/** The customer's own copy of the offer (D-288 ruling 4): `GET
 * /v1/public/quotes/{token}/pdf` answers 302 to a stored PDF, never a fresh
 * render, so the customer always gets the exact document their manager sent.
 * On the valid page that is the PDF that went out WITH this link. Once the
 * quote is accepted, EVERY link of it — an older one still in the customer's
 * chat included — serves the PDF of the send they accepted through, which is
 * why the accepted page can offer it whichever link opened it. A plain link,
 * because the browser follows the redirect on its own and this page holds no
 * session to carry.
 *
 * Rendered on the valid page and the accepted page only, the two states the
 * door serves. It is never on the expired or superseded page: the door refuses
 * both with a 410, and a lapsed or replaced offer on paper invites acting on
 * it.
 *
 * `noreferrer` matters more here than anywhere else in the product: the token
 * is in the URL of both this page and the link, and a Referer header would hand
 * the credential to whatever the PDF viewer loads next. */
function QuotePdfLink({ token }: { token: string }) {
  const { t } = useTranslation();
  return (
    <p className="public-doc">
      <a
        className="public-link"
        href={`/v1/public/quotes/${encodeURIComponent(token)}/pdf`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="public-quote-pdf"
      >
        {t("public.quote.pdf")}
      </a>
    </p>
  );
}

export function PublicQuoteScreen() {
  const { token } = useParams({ strict: false });
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const query = usePublicQuote(token ?? "");
  const view: PublicQuoteView | undefined = query.data;

  const [confirming, setConfirming] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declined, setDeclined] = useState(false);
  const accept = useAcceptPublicQuote(token ?? "");
  const decline = useDeclinePublicQuote(token ?? "");

  // The DOCUMENT's locale drives the render (§4.10) — see publicRenderLocale
  // for what happens to a placeholder locale, and why the browser's own
  // language never wins here.
  const locale =
    view !== undefined && "locale" in view ? publicRenderLocale(view.locale) : undefined;

  // NO SUPERSEDED AUTO-FORWARD, and the reason is on the contract's own
  // `superseded` member: `quote_accept_tokens.token_hash` stores sha256 of the
  // customer's credential and the raw token is never written down, so nothing
  // in the database can reconstruct the newer link. §4.10's "auto-forward" was
  // amended in this slice rather than built, because building it would mean
  // storing raw acceptance credentials in the database, its backups and its
  // query logs to save one hop the customer can make by opening the message
  // that carried the new offer.
  //
  // `isSafeInternalPath` still guards the ACCEPTED state's `tracking_path`
  // below — that one is a path we mint, and it is checked anyway, because a
  // payload-driven navigation on an unauthenticated page customers reach from
  // a Telegram message is a phishing primitive whether or not we believe the
  // payload.

  if (token === undefined || token.length === 0) {
    return (
      <PublicShell data-testid="public-quote-screen">
        <PublicFailure
          title={t("public.quote.notFoundTitle")}
          body={t("public.quote.notFoundBody")}
          data-testid="public-quote-not-found"
        />
      </PublicShell>
    );
  }

  if (view === undefined) {
    return (
      <PublicShell data-testid="public-quote-screen">
        <CardSkeleton lines={6} label={t("public.shell.loading")} />
      </PublicShell>
    );
  }

  // ---- the five states -------------------------------------------------
  if (view.state === "error") {
    return (
      <PublicShell data-testid="public-quote-screen">
        <PublicFailure
          title={t("public.quote.errorTitle")}
          body={t("public.quote.errorBody")}
          requestId={view.requestId}
          onRetry={() => void query.refetch()}
          data-testid="public-quote-error"
        />
      </PublicShell>
    );
  }

  if (view.state === "not_found") {
    // NO seller name, NO locale switch, NO branding — the generic 404. The
    // shell is entered without `sellerName` for exactly this reason.
    return (
      <PublicShell data-testid="public-quote-screen">
        <section className="public-card public-card--quiet" data-testid="public-quote-not-found">
          <h1 className="public-title">{t("public.quote.notFoundTitle")}</h1>
          <p className="public-body">{t("public.quote.notFoundBody")}</p>
        </section>
      </PublicShell>
    );
  }

  if (view.state === "expired") {
    return (
      <PublicShell
        locale={locale}
        sellerName={view.seller.display_name}
        data-testid="public-quote-screen"
      >
        {/* TWO WAYS AN OFFER DIES, TWO SENTENCES (D-268 ruling 3). The
            validity ran OUT — the 02:00 cron's case — and the page dates it.
            Or the manager WITHDREW it early on §4.5's bulk bar, and there is
            no date to state: §2.12 carries no expired-at stamp, so `expired_on`
            is still the END OF THE WINDOW, which on a withdrawn quote is up to
            a fortnight in the FUTURE. That is the shape this shipped in, and it
            told a customer in their own language that the offer they can no
            longer accept is valid until a date that has not arrived. The server
            answers WHICH death it was in `withdrawn`; the date is printed only
            when it is false. */}
        <section className="public-card" data-testid="public-quote-expired">
          <h1 className="public-title">
            {view.withdrawn ? t("public.quote.withdrawnTitle") : t("public.quote.expiredTitle")}
          </h1>
          {view.withdrawn ? null : (
            <p className="public-body" data-testid="public-quote-expired-on">
              {t("public.quote.expiredOn", {
                date: formatBusinessDate(view.expired_on, i18n.language),
              })}
            </p>
          )}
          {/* The ask is the same either way, and it is the only useful act
              this page has: the manager can send a new offer in a minute. */}
          <p className="public-body">{t("public.quote.expiredAsk")}</p>
          <ManagerContact seller={view.seller} />
        </section>
      </PublicShell>
    );
  }

  if (view.state === "superseded") {
    // Rendered while the forward runs, AND as the terminal page when there is
    // nothing to forward to. Both readings are correct copy for what happened.
    return (
      <PublicShell
        locale={locale}
        sellerName={view.seller.display_name}
        data-testid="public-quote-screen"
      >
        <section className="public-card" data-testid="public-quote-superseded">
          <h1 className="public-title">{t("public.quote.supersededTitle")}</h1>
          <p className="public-body">{t("public.quote.supersededBody")}</p>
          {/* The DATE the newer offer went out is the useful fact: it is how a
              customer tells whether the newer message is one they have already
              seen, and it is the only thing this page can honestly offer in
              place of the link it cannot reconstruct. */}
          <p className="public-body" data-testid="public-quote-superseded-at">
            {t("public.quote.supersededOn", {
              date: formatTimestamp(view.superseded_at, i18n.language),
            })}
          </p>
          <p className="public-body">{t("public.quote.supersededAsk")}</p>
          <ManagerContact seller={view.seller} />
        </section>
      </PublicShell>
    );
  }

  if (view.state === "accepted") {
    return (
      <PublicShell
        locale={locale}
        sellerName={view.seller.display_name}
        data-testid="public-quote-screen"
      >
        <section className="public-card" data-testid="public-quote-accepted">
          <h1 className="public-title">{t("public.quote.acceptedTitle")}</h1>
          <p className="public-lede" data-testid="public-order-no">
            {t("public.quote.acceptedNumber", { orderNo: view.order_no })}
          </p>
          <p className="public-meta">
            {t("public.quote.acceptedOn", {
              date: formatTimestamp(view.accepted_at, i18n.language),
            })}
          </p>
          {isSafeInternalPath(view.tracking_path) ? (
            <Button
              variant="primary"
              className="public-action"
              data-testid="public-track-link"
              onClick={() => {
                void navigate({ to: view.tracking_path });
              }}
            >
              {t("public.quote.trackOrder")}
            </Button>
          ) : null}
          <QuotePdfLink token={token} />
          <ManagerContact seller={view.seller} />
        </section>
      </PublicShell>
    );
  }

  // ---- valid -----------------------------------------------------------
  const countdown = countdownFrom(view.server_time, view.valid_until);
  const money = (minor: number) => formatMinor(minor, view.currency, i18n.language);

  // The accept succeeded: render the confirmation IN PLACE. Navigating away
  // to a fresh read would cost a round trip on a phone connection at the one
  // moment the customer is waiting on us, and the payload we already hold
  // names the order.
  if (accept.isSuccess) {
    return (
      <PublicShell
        locale={locale}
        sellerName={view.seller.display_name}
        data-testid="public-quote-screen"
      >
        <section className="public-card" data-testid="public-quote-accepted">
          <h1 className="public-title">{t("public.quote.acceptedTitle")}</h1>
          <p className="public-lede" data-testid="public-order-no">
            {t("public.quote.acceptedNumber", { orderNo: accept.data.order_no })}
          </p>
          {isSafeInternalPath(accept.data.tracking_path) ? (
            <Button
              variant="primary"
              className="public-action"
              data-testid="public-track-link"
              onClick={() => {
                void navigate({ to: accept.data.tracking_path });
              }}
            >
              {t("public.quote.trackOrder")}
            </Button>
          ) : null}
          {/* The accept just spent this token INTO the accepted state, which
              the PDF door serves: the same copy the read-side accepted page
              offers. */}
          <QuotePdfLink token={token} />
          <ManagerContact seller={view.seller} />
        </section>
      </PublicShell>
    );
  }

  if (declined || decline.isSuccess) {
    return (
      <PublicShell
        locale={locale}
        sellerName={view.seller.display_name}
        data-testid="public-quote-screen"
      >
        <section className="public-card public-card--quiet" data-testid="public-quote-declined">
          <h1 className="public-title">{t("public.quote.declined")}</h1>
          <ManagerContact seller={view.seller} />
        </section>
      </PublicShell>
    );
  }

  // §9.1's double-tap loser, an already-used token, and a quote that lapsed or
  // was superseded under the customer's thumb all answer 410. None of them is
  // a failure to report — the token is spent, and what the customer is owed is
  // the state it is spent INTO: their order number and tracking link for a used
  // token, the expired or superseded page otherwise.
  //
  // THAT PAGE HAS ALREADY BEEN FETCHED BY THE TIME THIS LINE RUNS. The accept
  // hook re-resolves the read on a 410 and `useMutation` awaits `onError`
  // before it dispatches the error, so `view` above is the door's new answer
  // and one of the resolved branches has already returned. Reaching here means
  // the POST refused the token while the GET still calls the offer live — the
  // door contradicting itself, which no ordinary tap can produce.
  //
  // So this is a terminal, not the normal path, and the verbs come off it. The
  // shape this shipped in (P3-C-1 audit row 37) made this the ONLY thing a 410
  // produced: a one-line alert inside the still-valid page, under an Accept, a
  // Decline and a confirm Yes that all stayed pressable over a dead token, with
  // no order number and no tracking link anywhere on it.
  const acceptGone = accept.error instanceof RequestError && accept.error.httpStatus === 410;

  return (
    <PublicShell
      locale={locale}
      sellerName={view.seller.display_name}
      data-testid="public-quote-screen"
    >
      <section className="public-card" data-testid="public-quote-valid">
        <h1 className="public-title" data-testid="public-quote-no">
          {t("public.quote.heading", { quoteNo: view.quote_no })}
        </h1>
        <p className="public-meta">
          {t("public.quote.forCustomer", { customer: view.customer_display_name })}
        </p>

        {/* The countdown, computed off SERVER time — never the device clock
            (lib/public-view.countdownFrom says why).

            `days` is 0 ON the last acceptable day, so that is where "Last day
            to accept" goes. It used to sit at `days === 1` — the day BEFORE
            expiry — with a second string, "This offer expires today", holding
            day 0. So a customer with a clear day in hand was told it was their
            last, and the actual last day said something else: one deadline
            announced twice, a day early, on the one page whose whole purpose
            is a decision made in time. Two strings for one boundary is what
            made the off-by-one invisible, so there is now one. */}
        <p className="public-validity" data-testid="public-validity">
          <StatusChip
            variant={countdown?.urgent === true ? "warn" : "neutral"}
            data-testid="public-validity-chip"
          >
            {countdown === undefined
              ? t("public.quote.validUntil", {
                  date: formatBusinessDate(view.valid_until, i18n.language),
                })
              : countdown.days === 0
                ? t("public.quote.lastDay")
                : t("public.quote.daysLeft", { count: countdown.days })}
          </StatusChip>{" "}
          {t("public.quote.validUntil", {
            date: formatBusinessDate(view.valid_until, i18n.language),
          })}
        </p>

        {/* THE LINES. A definition list, not a table: on the phone this page
            is read on, a four-column table of cable descriptions either
            scrolls sideways or truncates the one column that identifies the
            goods. Each line is a labelled block that stacks; on a wide screen
            the grid puts the numbers in a right-aligned column. */}
        <ul className="public-lines" data-testid="public-quote-lines">
          {view.lines.map((line) => (
            <li className="public-line" key={line.line_no} data-testid="public-quote-line">
              <p className="public-line-name">{line.description}</p>
              <dl className="public-line-facts">
                <dt>{t("public.quote.colQty")}</dt>
                <dd className="num">
                  {line.qty} {line.uom_code}
                </dd>
                <dt>{t("public.quote.colUnitPrice")}</dt>
                <dd className="num">{money(line.unit_price_minor)}</dd>
                <dt>{t("public.quote.colTotal")}</dt>
                <dd className="num">{money(line.line_total_minor)}</dd>
              </dl>
              {/* The date module 03 quoted for this line (spec 09 §4.10,
                  D-465 ruling 5), stated as confirmed on acceptance. A line
                  without one, or a page from an API build before J-2, says
                  nothing about a date. */}
              {typeof line.delivery_by === "string" ? (
                <p
                  className="public-line-delivery"
                  data-testid="public-quote-line-delivery"
                  data-date={line.delivery_by}
                >
                  {t("public.quote.deliveryBy", {
                    date: formatBusinessDate(line.delivery_by, i18n.language),
                  })}
                </p>
              ) : null}
            </li>
          ))}
        </ul>

        {/* Totals. The VAT line states the MODE (D-234), because "1 160 000 ₸"
            means two different things to a buyer depending on whether the tax
            is inside it, and every Kazakh commercial offer says which. */}
        <dl className="public-totals" data-testid="public-quote-totals">
          <dt>{t("public.quote.subtotal")}</dt>
          <dd className="num">{money(view.subtotal_minor)}</dd>
          <dt>
            {view.vat_mode === "inclusive"
              ? t("public.quote.vatInclusive", { rate: view.vat_rate_pct })
              : t("public.quote.vatExclusive", { rate: view.vat_rate_pct })}
          </dt>
          <dd className="num">{money(view.tax_minor)}</dd>
          <dt className="public-total-label">{t("public.quote.total")}</dt>
          <dd className="num public-total-value" data-testid="public-quote-total">
            {money(view.total_minor)}
          </dd>
        </dl>

        {view.note_to_customer !== null && view.note_to_customer.length > 0 ? (
          <div className="public-note" data-testid="public-quote-note">
            <p className="public-note-label">{t("public.quote.note")}</p>
            <p className="public-body">{view.note_to_customer}</p>
          </div>
        ) : null}

        <QuotePdfLink token={token} />

        {accept.isError && !acceptGone ? (
          <p className="public-alert" role="alert" data-testid="public-accept-failed">
            {t("public.quote.acceptFailed")}
          </p>
        ) : null}

        {/* ONE confirmation step before a press with legal weight. Not a
            browser confirm(): it is unstyleable, untranslatable through our
            dictionary, and on iOS Safari it is a system sheet that reads in
            the OS language rather than the customer's. */}
        {acceptGone ? (
          // The gone terminal, and it stands FIRST in this chain on purpose:
          // it takes the place of the confirm panel and of both verbs, so a
          // token the server has already refused cannot be offered again.
          <p className="public-alert" role="alert" data-testid="public-accept-gone">
            {t("public.quote.acceptGoneTitle")}
          </p>
        ) : confirming ? (
          <div className="public-confirm" data-testid="public-accept-confirm">
            <p className="public-confirm-title">{t("public.quote.confirmTitle")}</p>
            <p className="public-body">{t("public.quote.confirmBody")}</p>
            <div className="public-actions">
              <Button
                variant="primary"
                busy={accept.isPending}
                busyLabel={t("public.quote.accepting")}
                data-testid="public-accept-yes"
                onClick={() => {
                  accept.mutate();
                }}
              >
                {t("public.quote.confirmYes")}
              </Button>
              <Button
                data-testid="public-accept-no"
                onClick={() => {
                  setConfirming(false);
                }}
              >
                {t("public.quote.confirmNo")}
              </Button>
            </div>
          </div>
        ) : declining ? (
          <div className="public-confirm" data-testid="public-decline-panel">
            <p className="public-confirm-title">{t("public.quote.declineTitle")}</p>
            <p className="public-body">{t("public.quote.declineBody")}</p>
            <div className="public-actions public-actions--wrap">
              {DECLINE_REASONS.map((reason) => (
                <Button
                  key={reason.value}
                  busy={decline.isPending}
                  data-testid={`public-decline-${reason.value}`}
                  onClick={() => {
                    decline.mutate(
                      { reason_code: reason.value },
                      {
                        onSuccess: () => {
                          setDeclined(true);
                        },
                      },
                    );
                  }}
                >
                  {t(reason.key)}
                </Button>
              ))}
            </div>
            <div className="public-actions">
              {/* A reason is never required: a mandatory field on a
                  customer-facing decline is a field that produces garbage. */}
              <Button
                data-testid="public-decline-skip"
                busy={decline.isPending}
                onClick={() => {
                  decline.mutate(
                    {},
                    {
                      onSuccess: () => {
                        setDeclined(true);
                      },
                    },
                  );
                }}
              >
                {t("public.quote.declineSkip")}
              </Button>
              <Button
                data-testid="public-decline-cancel"
                onClick={() => {
                  setDeclining(false);
                }}
              >
                {t("public.quote.confirmNo")}
              </Button>
            </div>
            {decline.isError ? (
              <p className="public-alert" role="alert" data-testid="public-decline-failed">
                {t("public.quote.declineFailed")}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="public-actions" data-testid="public-quote-actions">
            <Button
              variant="primary"
              className="public-action"
              data-testid="public-accept"
              onClick={() => {
                setConfirming(true);
              }}
            >
              {t("public.quote.accept")}
            </Button>
            <Button
              data-testid="public-decline"
              onClick={() => {
                setDeclining(true);
              }}
            >
              {t("public.quote.decline")}
            </Button>
          </div>
        )}

        <ManagerContact seller={view.seller} />
      </section>
    </PublicShell>
  );
}
